/**
 * OrganizationSettings - Allows organization admins to configure their own organization
 * Features:
 * - Upload/change logo
 * - Configure organization settings (theme, branding, etc.)
 * - Update contact information
 * - Custom settings (stored in JSONB settings column)
 * 
 * Access: Organization admins (system_admin role within their organization)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Upload as UploadIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const OrganizationSettings = () => {
  const { user, organizationId, organizationSlug } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [organization, setOrganization] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    phone_number: '',
    address: '',
    timezone: 'America/Toronto',
    logo_url: '',
    settings: {
      primary_color: '#DC2626',
      secondary_color: '#1F2937',
      show_logo: true,
      custom_fields: {}
    }
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Check if user is organization admin
  useEffect(() => {
    if (user && user.role !== 'system_admin') {
      navigate('/dashboard', { 
        state: { error: 'Access denied. Organization Admin privileges required.' }
      });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (organizationId || user?.organization_id) {
      fetchOrganization();
    }
  }, [organizationId, user?.organization_id]);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const orgId = organizationId || user?.organization_id;
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/organizations/${orgId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const org = response.data;
      setOrganization(org);
      
      // Merge existing settings with defaults
      const settings = org.settings || {};
      setFormData({
        name: org.name || '',
        contact_email: org.contact_email || '',
        phone_number: org.phone_number || '',
        address: org.address || '',
        timezone: org.timezone || 'America/Toronto',
        logo_url: settings.logo_url || '',
        settings: {
          primary_color: settings.primary_color || '#DC2626',
          secondary_color: settings.secondary_color || '#1F2937',
          show_logo: settings.show_logo !== false,
          custom_fields: settings.custom_fields || {},
          ...settings
        }
      });

      if (settings.logo_url) {
        setLogoPreview(settings.logo_url);
      }
      
      setError('');
    } catch (err) {
      console.error('Error fetching organization:', err);
      setError(err.response?.data?.error || 'Failed to load organization settings');
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    try {
      setError('');
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', logoFile);
      formData.append('organization_id', organizationId || user?.organization_id);

      // Upload logo
      // Note: You'll need to create an upload endpoint, or store as base64 in settings
      // For now, we'll use base64 encoding
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Logo = reader.result;
        
        // Update settings with logo
        const updatedSettings = {
          ...formData.settings,
          logo_url: base64Logo,
          logo_uploaded_at: new Date().toISOString()
        };

        try {
          await handleSave({ settings: updatedSettings });
          setLogoFile(null);
          setUploadDialogOpen(false);
        } catch (err) {
          setError('Failed to upload logo');
        }
      };
      reader.readAsDataURL(logoFile);
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('Failed to upload logo');
    }
  };

  const handleSave = async (overrideData = null) => {
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      const orgId = organizationId || user?.organization_id;
      
      const dataToSave = overrideData || {
        name: formData.name,
        contact_email: formData.contact_email,
        phone_number: formData.phone_number,
        address: formData.address,
        timezone: formData.timezone,
        settings: formData.settings
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/organizations/${orgId}`,
        dataToSave,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Settings saved successfully!');
      setOrganization(response.data);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.startsWith('settings.')) {
      const settingKey = field.replace('settings.', '');
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          [settingKey]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, my: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <SettingsIcon fontSize="large" color="primary" />
          <Typography variant="h4">
            Organization Settings
          </Typography>
          {organization && (
            <Chip label={organization.name} color="primary" variant="outlined" />
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Logo Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Organization Logo
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 2 }}>
                  <Avatar
                    src={logoPreview || formData.settings.logo_url}
                    alt={formData.name}
                    sx={{ width: 120, height: 120 }}
                    variant="rounded"
                  >
                    <BusinessIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      sx={{ mb: 1 }}
                    >
                      Upload Logo
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Recommended: 200x200px, max 5MB
                    </Typography>
                    {logoFile && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{ mt: 1 }}
                      >
                        Save Logo
                      </Button>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Church Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phone_number}
                      onChange={(e) => handleInputChange('phone_number', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      multiline
                      rows={2}
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={formData.timezone}
                        onChange={(e) => handleInputChange('timezone', e.target.value)}
                        label="Timezone"
                      >
                        <MenuItem value="America/Toronto">Eastern Time (Toronto)</MenuItem>
                        <MenuItem value="America/Winnipeg">Central Time (Winnipeg)</MenuItem>
                        <MenuItem value="America/Vancouver">Pacific Time (Vancouver)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Branding Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Branding & Appearance
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Primary Color"
                      type="color"
                      value={formData.settings.primary_color}
                      onChange={(e) => handleInputChange('settings.primary_color', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Secondary Color"
                      type="color"
                      value={formData.settings.secondary_color}
                      onChange={(e) => handleInputChange('settings.secondary_color', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.settings.show_logo}
                          onChange={(e) => handleInputChange('settings.show_logo', e.target.checked)}
                        />
                      }
                      label="Show Logo on Public Forms"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => fetchOrganization()}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => handleSave()}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Logo Upload Confirmation Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload Logo</DialogTitle>
        <DialogContent>
          {logoPreview && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img 
                src={logoPreview} 
                alt="Logo preview" 
                style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
              />
            </Box>
          )}
          <Typography>
            Are you sure you want to upload this logo? It will replace your current logo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLogoUpload} variant="contained" color="primary">
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationSettings;

